import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { articles, categories, siteInfo } from "@/mocks/articles";

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatViews(views: number): string {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
  return `${views}`;
}

function getCategoryName(slug: string): string {
  return categories.find((c) => c.slug === slug)?.name || "八卦";
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950">文章不存在</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 4);

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.summary,
          url: `${siteInfo.domain}/article/${article.slug}`,
          datePublished: article.publishedAt,
          author: {
            "@type": "Person",
            name: article.author,
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: siteInfo.domain,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: getCategoryName(article.category),
              item: `${siteInfo.domain}/category/${article.category}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: article.title,
            },
          ],
        }}
      />
      <Navbar />
      <main className="min-h-screen">
        <article>
          <div className="pt-20 md:pt-24 pb-0 bg-background-50">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: getCategoryName(article.category), href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="pt-6 md:pt-8 pb-10 bg-background-50">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                <div className="lg:w-[60%]">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-foreground-950"></span>
                    <span className="text-[11px] font-medium text-foreground-500">独家深度</span>
                  </div>

                  <h1 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-foreground-950 leading-tight">
                    <span className="text-foreground-400 text-lg md:text-xl">&ldquo;</span>
                    {article.title}
                    <span className="text-foreground-400 text-lg md:text-xl">&rdquo;</span>
                  </h1>

                  <div className="flex items-center gap-3 mt-5 pb-6 border-b border-background-200/70">
                    <div className="w-9 h-9 rounded-full bg-secondary-500 flex items-center justify-center">
                      <i className="ri-user-line text-background-50 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        <span className="text-[11px] text-foreground-500">已验证爆料人</span>
                      </div>
                    </div>
                  </div>

                  <div
                    className="mt-6 prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  <div className="mt-8 flex flex-wrap items-center gap-2 pt-4 border-t border-background-200/70">
                    <span className="text-xs text-foreground-500 mr-1">标签：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 text-[11px] font-medium bg-background-100 text-foreground-600 rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-6 flex items-center gap-4 text-xs text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {formatTime(article.publishedAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {formatViews(article.views)} 阅读
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line"></i>
                      {formatViews(article.comments)} 评论
                    </span>
                  </div>
                </div>

                <div className="lg:w-[40%]">
                  <div className="rounded-lg overflow-hidden sticky top-24">
                    <img
                      src="https://readdy.ai/api/search-image?query=Dramatic%20editorial%20black%20and%20white%20portrait%20of%20a%20silhouette%20figure%20with%20head%20slightly%20tilted%20upward%2C%20looking%20toward%20light%2C%20pure%20white%20background%2C%20cinematic%20side%20lighting%20creating%20strong%20contrast%2C%20mysterious%20and%20contemplative%20mood%2C%20editorial%20fashion%20photography%20style%2C%20high%20contrast%20monochrome%2C%20no%20identifiable%20face%20features%2C%20artistic%20abstraction&width=800&height=1200&seq=article-detail-side&orientation=portrait"
                      alt="文章配图"
                      className="w-full h-auto object-cover rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg md:text-xl font-extrabold text-foreground-950">
                    相关<span className="text-accent-500">推荐</span>
                  </h2>
                  <p className="text-sm text-foreground-500 mt-0.5">更多同类爆料等你来看</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard
                    key={a.id}
                    slug={a.slug}
                    title={a.title}
                    category={a.category}
                    coverImageSeq={a.coverImageSeq}
                    thumbnailPrompt={a.thumbnailPrompt}
                    publishedAt={a.publishedAt}
                    views={a.views}
                    isHot={a.isHot}
                    isBreaking={a.isBreaking}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[800px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-lg md:text-xl font-extrabold text-foreground-950">
              觉得不够过瘾？更多<span className="text-accent-500">大瓜</span>等你来挖
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-5">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm rounded-lg transition-all"
              >
                <i className="ri-home-line"></i>
                回到首页
              </Link>
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-2 px-6 py-3 border border-background-300/60 hover:border-background-400 text-foreground-700 font-semibold text-sm rounded-lg transition-all"
              >
                更多{getCategoryName(article.category)}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}